import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { Button, Tooltip } from '@mui/material';
import React from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { CART_ACTION_BUTTON_SX } from '../MainPageLayout/cartActionButton';
import {
  selectHasCopyableOrderLines,
  selectIsDraftCartEmpty,
} from '../../redux/features/draftCart/draftCart.selector';
import { copyOrderIntoDraftCart } from '../../redux/features/draftCart/draftCart.thunks';

/**
 * HITL-1649 — the label names the DESTINATION, because the control now sits on
 * the source.
 *
 * It read "Copy all" while it lived in My Cart's header, where the column it was
 * standing in was the destination and naming it would have been redundant. On
 * the Live order it is the other way round: "Copy all" alone would leave the
 * agent to work out which of the two columns the items land in.
 *
 * "Live Order" rather than "AI Cart" in the tooltips: HITL-1614 renamed the
 * column, so "AI Cart" names nothing on screen any more.
 */
export const COPY_ALL_LABEL = 'Copy all to My Cart';
export const COPY_ALL_TOOLTIP =
  'Copy every item from the Live Order into My Cart';
/**
 * HITL-1649 — the tooltip says REPLACES, because the press does.
 *
 * Each press re-seeds My Cart from the Live order, discarding whatever was
 * there. There is no confirmation — a dialog in the seconds before readback is
 * friction exactly where it costs most — so this sentence is the whole of the
 * warning, and it is only shown in the state where there is something to lose.
 */
export const COPY_ALL_REPLACE_TOOLTIP =
  'Replaces My Cart with every item from the Live Order';
/**
 * The one remaining disabled reason. The press is refused with nothing to copy
 * rather than emptying My Cart into silence, and a disabled control the agent
 * can hover explains itself at the moment they reach for it.
 */
export const COPY_ALL_EMPTY_ORDER_TOOLTIP =
  'Nothing in the Live Order to copy yet';

/**
 * HITL-1644 — take the whole Live order at once.
 *
 * HITL-1649 moved it out of My Cart's header and onto the foot of the LIVE
 * ORDER card. It is an action ON the Live order — it reads every line in that
 * column — and sitting in the other card's header it read as one of My Cart's
 * own controls, pointing at a source 400px away. The two columns now carry one
 * cart-level action each, in the same place, each naming where it sends to:
 *
 *     LIVE ORDER  ->  [ Copy all to My Cart ]
 *     MY CART     ->  [ Take over with My Cart ]
 *
 * Styled from `CART_ACTION_BUTTON_SX`, the same object the takeover uses, so
 * the two columns' actions are the same control in two places rather than two
 * controls that happen to resemble each other.
 *
 * `KeyboardDoubleArrowLeftIcon` — `<<` — points at the column the items are
 * going to, which from the Live order is My Cart on its left. Deliberately not
 * `ContentCopyOutlinedIcon`, which already means *Duplicate this line* in a My
 * Cart row.
 *
 * Disabled on one condition only: the Live order has nothing this copy would
 * take. The thunk refuses on exactly the same test, so the guarantee lives in
 * the state change rather than in this component — the control only makes the
 * refusal visible before it is attempted, and names the reason, because a
 * control disabled for a reason the agent cannot see is just a control that is
 * broken.
 *
 * It is NOT disabled by My Cart having items in it, which is how HITL-1644 kept
 * a second press from duplicating the order. Each press now REPLACES
 * (HITL-1649) — the agent asked why one press greyed the button out, and a
 * guard that reads as a fault is not doing its job. The cost is that a press
 * discards hand-built lines without asking; the tooltip changes to say
 * `Replaces` in exactly that state.
 *
 * `sx` rather than `makeStyles`: emotion hashes on content, so a brand-new
 * element cannot renumber the JSS counter behind an existing snapshot.
 */
const CopyAllToDraftControl = () => {
  const dispatch = useAppDispatch();
  const isDraftCartEmpty = useAppSelector(selectIsDraftCartEmpty);
  const hasCopyableOrderLines = useAppSelector(selectHasCopyableOrderLines);

  // Enabled whenever there is something to copy — My Cart's contents no longer
  // gate it, they are what the press replaces.
  const isDisabled = !hasCopyableOrderLines;
  const tooltip = !hasCopyableOrderLines
    ? COPY_ALL_EMPTY_ORDER_TOOLTIP
    : isDraftCartEmpty
    ? COPY_ALL_TOOLTIP
    : COPY_ALL_REPLACE_TOOLTIP;

  const onCopyAll = () => {
    dispatch(copyOrderIntoDraftCart());
  };

  return (
    // A disabled MUI Button fires no pointer events, so the Tooltip has to wrap
    // a span or the explanation never appears in the one state that needs it.
    // `display: block` so the span does not shrink-wrap the full-width button.
    <Tooltip title={tooltip} arrow disableInteractive>
      <span style={{ display: 'block' }}>
        <Button
          aria-label={COPY_ALL_TOOLTIP}
          data-testid="copy-all-to-draft"
          disabled={isDisabled}
          onClick={onCopyAll}
          variant="contained"
          size="small"
          disableElevation
          startIcon={<KeyboardDoubleArrowLeftIcon />}
          sx={CART_ACTION_BUTTON_SX}
        >
          {COPY_ALL_LABEL}
        </Button>
      </span>
    </Tooltip>
  );
};

export default CopyAllToDraftControl;
